/**
 * 경제(상점) 도메인 부트스트래퍼 (S2a) — 상점 컨테이너 뷰 생성·결선 + 탭 등록.
 * 상자 구매(EconomyCommands)·일일상점(DailyShopCommands)·광고제거(IapFlow)는 boot.commands로
 * 직접 결선 (shell 콜백 소거 — boot v2 규칙 3). 일일상점 탭 데이터도 여기서 조립.
 */
import { ShopContainerTabView } from "../ui/ShopContainerTabView.js";
import { buildDailyShopData, buildShopData } from "../ui/tabData.js";
import {
  DAILY_RESET_KEY_DAILY_SHOP,
  DAILY_RESET_KEY_SHOP_AD_DIAMOND,
} from "../models/ModuleBrickSaveData.js";
import { REMOVE_ADS_PACKAGE_PRODUCT_ID, REMOVE_ADS_PRODUCT_ID } from "../data/iapData.js";
import { SHOP_AD_PRODUCTS } from "../data/shopAdData.js";
import { viewTabHandle } from "@yjsgames/phaser-ui/tab-registry";
import { computeRarityOdds, GOLD_BOX_RARITY_WEIGHTS, MODULE_BRICK_BALANCE } from "../data/balance.js";
import { rarityLabel, RARITY_COLOR } from "../ui/uiTokens.js";
import { t } from "../i18n/i18n.js";
import { formatBig } from "../ui/formatNumber.js";
import { EconomyController } from "../systems/EconomyController.js";
import type { BigNumber } from "@yjsgames/bignumber";
import type { DomainBootstrapper } from "./BootTypes.js";

export const economyBoot: DomainBootstrapper = {
  key: "economy",
  install(boot) {
    const { tabContent } = boot.layout;
    const { economy, dailyShop, shopAd, iap } = boot.commands;
    // 상점 광고 다이아 표시 데이터 — cooldown 남은시간/오늘 수령 여부 주입(1초 틱·refresh 공용).
    const shopData = () =>
      buildShopData(
        boot.ctx.economy(),
        boot.ctx.goldBox(),
        boot.state.boxResult(),
        boot.state.adFree(),
        boot.cooldown(),
        boot.state.shopDailyAdDiamondClaimed(),
        {
          removeAds: iap.isProductAvailable(REMOVE_ADS_PRODUCT_ID),
          removeAdsPackage: iap.isProductAvailable(REMOVE_ADS_PACKAGE_PRODUCT_ID),
          removeAdsPriceLabel: iap.productPriceLabel(REMOVE_ADS_PRODUCT_ID),
          removeAdsPackagePriceLabel: iap.productPriceLabel(REMOVE_ADS_PACKAGE_PRODUCT_ID),
        },
        shopAd.adProcessing(),
        boot.ctx.rebirth(),
      );
    // 골드 상자 확률(?) 팝업 — 등급 드롭율(computeRarityOdds) + 천장 안내를 표시 데이터로 합성해 ModalHost에 위임.
    const showGoldBoxOdds = (): void => {
      const screen = boot.screen();
      boot.modals.openGoldBoxOdds({
        screenWidth: screen.width,
        screenHeight: screen.height,
        title: t("shop.odds.title"),
        rows: computeRarityOdds(GOLD_BOX_RARITY_WEIGHTS).map((o) => ({
          label: rarityLabel(o.rarity),
          color: RARITY_COLOR[o.rarity],
          percentText: `${o.percent.toFixed(1)}%`,
        })),
        pityText: t("shop.odds.pity", {
          interval: MODULE_BRICK_BALANCE.pityInterval,
          rarity: rarityLabel(MODULE_BRICK_BALANCE.pityGuaranteeRarity),
        }),
        pityRemainingText: t("shop.odds.pityRemaining", {
          remaining: MODULE_BRICK_BALANCE.pityInterval - boot.ctx.goldBox().rarityPityCounter,
        }),
        closeText: t("shop.odds.close"),
        onClose: () => boot.modals.closeGoldBoxOdds(),
      });
    };
    // 구매 확인 모달 — 재화 소비 상품(골드 1개/10개·다이아) 오클릭 방지. Yes에서만 실제 command 호출.
    // AutoPlay api(buyGoldBox 등)는 모달 우회 직행 유지 — UI 버튼만 이 경로를 거친다.
    const confirmBuy = (productName: string, costIconKey: string, cost: BigNumber, buy: () => void): void => {
      const screen = boot.screen();
      boot.modals.openPurchaseConfirm({
        screenWidth: screen.width,
        screenHeight: screen.height,
        productName,
        costIconKey,
        costText: formatBig(cost),
        onConfirm: () => {
          boot.modals.closePurchaseConfirm();
          buy();
        },
        onCancel: () => boot.modals.closePurchaseConfirm(),
      });
    };
    // 상점 컨테이너 — [상점]/[일일상점] 서브탭 통합. bottomY = 패널 배경 하단(tabPanelBottom) — 일반 탭과 동일 기준.
    // tabContent 하단(패널 inset)이 아니라 패널 하단을 써야 서브탭 바가 메인 하단 탭바에 바짝 붙는다(사용자 요청).
    const view = new ShopContainerTabView(
      boot.scene,
      tabContent.x,
      tabContent.y,
      tabContent.width,
      boot.layout.shell.tabPanelBottom,
      {
        shop: {
          onBuyGoldBox: () =>
            confirmBuy(
              t("shop.goldBoxDesc"),
              "icon_gold",
              EconomyController.goldBoxCost(boot.ctx.goldBox().boxOpenCount),
              () => economy.buyGoldBox(),
            ),
          onBuyGoldBox10: () =>
            confirmBuy(
              t("shop.goldBox10Desc"),
              "icon_gold",
              EconomyController.goldBox10Cost(boot.ctx.goldBox().boxOpenCount),
              () => economy.buyGoldBox10(),
            ),
          onShowGoldBoxOdds: showGoldBoxOdds,
          onBuyDiamondBox: () =>
            confirmBuy(
              t("shop.diamondBoxDesc"),
              "icon_diamond",
              EconomyController.diamondBoxCost(),
              () => economy.buyDiamondBox(),
            ),
          onBuyRebirthConditionReset: () =>
            confirmBuy(
              t("shop.rebirthConditionResetDesc", {
                chapter: boot.ctx.rebirth().initialRequiredChapter(),
              }),
              "icon_diamond",
              EconomyController.rebirthConditionResetCost(),
              () => economy.buyRebirthConditionReset(),
            ),
          onBuyRemoveAds: () => iap.buyRemoveAds(),
          onBuyRemoveAdsPackage: () => iap.buyRemoveAdsPackage(),
          // 광고제거 상품 행(단독+패키지) 노출 — 웹(dev·live)은 featureFlags.iapRemoveAds=false로 숨김(itch.io 출시용).
          removeAdsEnabled: boot.features.iapRemoveAds,
          // 광고 다이아 행 — 광고 시청 보상(쿨타임/오늘). 클릭 직행(광고가 확인 게이트라 confirmBuy 불요).
          onBuyAdDiamond: (id) => shopAd.buyAdDiamond(id),
          adProducts: SHOP_AD_PRODUCTS.map((p) => ({
            id: p.id,
            descKey: p.descKey,
            iconKey: p.iconKey,
            buttonIconKey: p.buttonIconKey,
            rewardAmount: p.rewardAmount,
          })),
        },
        dailyShop: {
          onBuySlot: (i) => dailyShop.buySlot(i),
          onReroll: () => dailyShop.reroll(),
        },
        dailyShopEnabled: boot.features.dailyShop,
        onSubChange: () => boot.tabs.refresh("shop"),
      },
    );
    boot.guideQuestHighlight.setShopGoldBoxRectProvider(() => view.goldBoxButtonRect());
    boot.addDisposer(() => boot.guideQuestHighlight.setShopGoldBoxRectProvider(null));
    // 서브탭 둘 다 갱신 — 기존 refreshAll 의미 보존(활성 서브탭 분기보다 단순·과갱신 무해).
    boot.tabs.register(
      "shop",
      viewTabHandle(view, () => {
        view.updateShop(shopData());
        if (boot.features.dailyShop) {
          view.updateDaily(
            buildDailyShopData(
              boot.ctx.dailyShop(),
              boot.dailyReset(),
              boot.ctx.economy(),
              boot.ctx.chapter(),
              dailyShop.lastResultText(),
              boot.state.adFree(),
              dailyShop.adProcessing(),
            ),
          );
        }
      }),
    );
    // 자정 리셋 — 일일상점 6슬롯 재추첨 + 리롤 누진 초기화.
    boot.dailyResets.register(DAILY_RESET_KEY_DAILY_SHOP, () => boot.ctx.dailyShop().applyReset());
    // 자정 리셋 — 오늘의 광고 다이아 수령 플래그 복귀(다시 1회 수령 가능).
    boot.dailyResets.register(DAILY_RESET_KEY_SHOP_AD_DIAMOND, () =>
      boot.commands.shopAd.resetDailyClaimed(),
    );
    // 광고 다이아 쿨타임 1초 틱 — regular 서브탭 활성 시에만 상점 행 갱신(카운트다운 mm:ss). 비활성 시 무동작.
    const cooldownTick = boot.scene.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        if (view.activeSub() !== "regular") return;
        view.updateShop(shopData());
      },
    });
    boot.addDisposer(() => cooldownTick.remove(false));
    // dev api — 상자/일일상점/광고제거/광고 다이아 (AutoPlay 동결 액션의 실제 구현).
    boot.api.register({
      buyGoldBox: () => economy.buyGoldBox(),
      buyGoldBox10: () => economy.buyGoldBox10(),
      buyDiamondBox: () => economy.buyDiamondBox(),
      buyRebirthConditionReset: () => economy.buyRebirthConditionReset(),
      buyRemoveAds: () => iap.buyRemoveAds(),
      buyRemoveAdsPackage: () => iap.buyRemoveAdsPackage(),
      buyDailyShopSlot: (i) => dailyShop.buySlot(i),
      rerollDailyShop: () => dailyShop.reroll(),
      buyShopAdDiamond: (id) => shopAd.buyAdDiamond(id),
      resetShopAdGates: () => shopAd.resetGates(),
    });
  },
};
